/**
 * ExportButton Component
 * 
 * A reusable dropdown button for exporting analytics data.
 * Provides options for CSV download, PDF download, and scheduling exports.
 */

import React, { useState } from "react";
import { Dropdown, Spinner } from "react-bootstrap";
import { Download, FiletypeCsv, FiletypePdf, Calendar3, Image } from "react-bootstrap-icons";
import styled from "styled-components";

import { exportAnalytics, ExportContext, ExportFormat, PdfExportOptions } from "../../services/export";
import Logger from "../../utils/Logger";

const StyledDropdown = styled(Dropdown)`
    .dropdown-toggle {
        display: flex;
        align-items: center;
        gap: 6px;
        background-color: transparent;
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: inherit;
        padding: 4px 12px;
        font-size: 14px;
        
        &:hover, &:focus {
            background-color: rgba(255, 255, 255, 0.1);
            border-color: rgba(255, 255, 255, 0.5);
        }
        
        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
    
    .dropdown-menu {
        min-width: 180px;
    }
    
    .dropdown-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        
        svg {
            font-size: 16px;
        }
    }
    
    .dropdown-divider {
        margin: 4px 0;
    }
`;

const SpinnerWrapper = styled.span`
    display: inline-flex;
    align-items: center;
`;

export type ExportButtonProps = {
    /** Context for generating exports */
    context: ExportContext;
    /** Whether export is enabled */
    enabled?: boolean;
    /** Whether scheduled exports are enabled */
    scheduledEnabled?: boolean;
    /** Callback when "Schedule Export" is clicked */
    onScheduleClick?: () => void;
    /** Custom button label */
    label?: string;
    /** Custom class name for styling */
    className?: string;
    /** Size variant */
    size?: "sm" | "lg";
};

/**
 * Export button with dropdown menu for CSV, PDF, and scheduling options
 */
const ExportButton: React.FC<ExportButtonProps> = ({
    context,
    enabled = true,
    scheduledEnabled = true,
    onScheduleClick,
    label = "Export",
    className,
    size
}) => {
    const [isExporting, setIsExporting] = useState(false);
    const [exportFormat, setExportFormat] = useState<ExportFormat | null>(null);
    const [isVisualExport, setIsVisualExport] = useState(false);

    // Check if visual PDF export is available (container ref is provided)
    const visualExportAvailable = !!context.getContainerRef;

    const handleExport = async (format: ExportFormat, options?: PdfExportOptions) => {
        if (!enabled || isExporting) return;

        setIsExporting(true);
        setExportFormat(format);
        setIsVisualExport(options?.visualMode || false);

        try {
            await exportAnalytics(context, format, options);
        } catch (error) {
            Logger.log("error", "ExportButton", `Failed to export ${format}`, error);
            // Error is logged, could add toast notification here
        } finally {
            setIsExporting(false);
            setExportFormat(null);
            setIsVisualExport(false);
        }
    };

    const handleScheduleClick = () => {
        if (onScheduleClick) {
            onScheduleClick();
        }
    };

    return (
        <StyledDropdown className={className}>
            <Dropdown.Toggle
                variant="outline-secondary"
                size={size}
                disabled={!enabled || isExporting}
                id="export-dropdown"
            >
                {isExporting ? (
                    <SpinnerWrapper>
                        <Spinner animation="border" size="sm" />
                    </SpinnerWrapper>
                ) : (
                    <Download />
                )}
                <span>{label}</span>
            </Dropdown.Toggle>

            <Dropdown.Menu>
                <Dropdown.Item
                    onClick={() => handleExport("csv")}
                    disabled={isExporting}
                >
                    {isExporting && exportFormat === "csv" ? (
                        <Spinner animation="border" size="sm" />
                    ) : (
                        <FiletypeCsv />
                    )}
                    <span>Download CSV</span>
                </Dropdown.Item>

                <Dropdown.Item
                    onClick={() => handleExport("pdf")}
                    disabled={isExporting}
                >
                    {isExporting && exportFormat === "pdf" && !isVisualExport ? (
                        <Spinner animation="border" size="sm" />
                    ) : (
                        <FiletypePdf />
                    )}
                    <span>{visualExportAvailable ? "Download PDF (Data)" : "Download PDF"}</span>
                </Dropdown.Item>

                {visualExportAvailable && (
                    <Dropdown.Item
                        onClick={() => handleExport("pdf", { visualMode: true })}
                        disabled={isExporting}
                    >
                        {isExporting && exportFormat === "pdf" && isVisualExport ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <Image />
                        )}
                        <span>Download PDF (Visual)</span>
                    </Dropdown.Item>
                )}

                {/* Schedule Export - temporarily hidden until feature is complete */}
                {false && scheduledEnabled && onScheduleClick && (
                    <>
                        <Dropdown.Divider />
                        <Dropdown.Item
                            onClick={handleScheduleClick}
                            disabled={isExporting}
                        >
                            <Calendar3 />
                            <span>Schedule Export</span>
                        </Dropdown.Item>
                    </>
                )}
            </Dropdown.Menu>
        </StyledDropdown>
    );
};

export default ExportButton;
